// Offers — compare active offers across 6 dimensions.
// Two data sources merged:
//   1. Manual entries in localStorage (with comp $ + 0–5 self-scored dims)
//   2. Applications at stage=Offer pulled from /api/data (placeholder cards
//      with "Fill in details →" button that promotes to localStorage)
// Manual entries take precedence for companies that appear in both.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, Pencil, GitCompare, Ban, Undo2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LogoSquare from '../components/ui/LogoSquare';
import Drawer from '../components/ui/Drawer';
import MarketBar from '../components/domain/MarketBar';
import { apiGet } from '../lib/api';
import { adaptDataResponse } from '../lib/adapt';
import { useSalaryBenchmark } from '../lib/useSalaryBenchmark';
import type { Application, Offer, OfferScores, OfferStatus } from '../lib/types';


const STORAGE_KEY = 'nice2job.offers';

const DIMS: (keyof OfferScores)[] = ['mission', 'growth', 'culture', 'comp', 'pace', 'remote'];

function loadOffers(): Offer[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Offer[];
    // Phase 14k: backfill missing status / createdAt so legacy entries
    // sort + render correctly without a one-off migration. `id` looks like
    // `offer-{Date.now()}` — we extract the timestamp to seed createdAt.
    return parsed.map((o) => {
      let createdAt = o.createdAt;
      if (typeof createdAt !== 'number') {
        const m = (o.id || '').match(/^offer-(\d{10,})$/);
        createdAt = m ? parseInt(m[1], 10) : Date.now();
      }
      return {
        ...o,
        status: o.status === 'rejected' ? 'rejected' : 'active',
        createdAt,
      };
    });
  } catch {
    return [];
  }
}
function saveOffers(offers: Offer[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(offers));
}

// Sort modes for the offers grid (Phase 14k). Persisted under
// `nice2job.offers.sort` so the user's choice survives refresh.
type SortMode = 'recent' | 'comp_desc' | 'comp_asc' | 'company';
const SORT_KEY = 'nice2job.offers.sort';
const SORT_LABEL: Record<SortMode, string> = {
  recent: 'Recently added',
  comp_desc: 'Total comp ↓',
  comp_asc: 'Total comp ↑',
  company: 'Company A→Z',
};
function loadSortMode(): SortMode {
  try {
    const raw = localStorage.getItem(SORT_KEY);
    if (raw === 'recent' || raw === 'comp_desc' || raw === 'comp_asc' || raw === 'company') return raw;
  } catch { /* noop */ }
  return 'recent';
}

// Shape for a row that comes from Applications (stage=Offer) but has no
// manual comp details yet. Used so the UI can render a placeholder card.
interface PendingOfferCard {
  kind: 'pending';
  app: Application;
}
interface FullOfferCard {
  kind: 'full';
  offer: Offer;
  appId?: string;        // if matched to an Application row, keep the id
}
type OfferCard = PendingOfferCard | FullOfferCard;

function companyKey(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export default function Offers() {
  const nav = useNavigate();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ company: string; role: string } | null>(null);
  // When editing is set, the drawer pre-fills ALL fields from this offer
  // (including base/equity/bonus/scores/location) and on save preserves
  // the original `id` instead of allocating a new one.
  const [editing, setEditing] = useState<Offer | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>(() => loadSortMode());

  // Persist sort choice on change
  useEffect(() => {
    try { localStorage.setItem(SORT_KEY, sortMode); } catch { /* noop */ }
  }, [sortMode]);

  useEffect(() => setOffers(loadOffers()), []);

  // Sort offers — rejected ALWAYS land at the bottom regardless of sort
  // mode (within rejected, we still apply the same secondary sort). This
  // keeps "active offers" at the top of the user's attention even if a
  // rejected one would otherwise rank higher (e.g. higher total comp).
  const sortedOffers = useMemo(() => {
    const cmp = (a: Offer, b: Offer): number => {
      switch (sortMode) {
        case 'comp_desc': return (b.total ?? 0) - (a.total ?? 0);
        case 'comp_asc':  return (a.total ?? 0) - (b.total ?? 0);
        case 'company':   return (a.company || '').localeCompare(b.company || '');
        case 'recent':
        default:          return (b.createdAt ?? 0) - (a.createdAt ?? 0);
      }
    };
    return [...offers].sort((a, b) => {
      const aRej = a.status === 'rejected';
      const bRej = b.status === 'rejected';
      if (aRej !== bRej) return aRej ? 1 : -1;
      return cmp(a, b);
    });
  }, [offers, sortMode]);

  // Toggle status active ↔ rejected. We don't add a separate confirm step
  // because the visual change is reversible — user can un-reject from the
  // same button. Cheap to flip.
  const toggleRejected = (id: string) => {
    const next = offers.map((o) =>
      o.id === id
        ? { ...o, status: o.status === 'rejected' ? 'active' as OfferStatus : 'rejected' as OfferStatus }
        : o
    );
    setOffers(next);
    saveOffers(next);
  };

  const dataQ = useQuery({
    queryKey: ['data'],
    queryFn: async () => adaptDataResponse(await apiGet('/api/data')),
  });

  const offerStageApps = (dataQ.data?.applications ?? []).filter((a) => a.stage === 'Offer');

  // Build the unified card list:
  //  - every manual offer (may or may not correspond to an Application)
  //  - every Application[stage=Offer] without a manual match yet (placeholder)
  const cards: OfferCard[] = useMemo(() => {
    const manualByCompany = new Map<string, Offer>();
    for (const o of sortedOffers) manualByCompany.set(companyKey(o.company), o);
    // Use the SORTED offer array — rejected offers slide to the bottom and
    // active ones lead with whatever sortMode is set to.
    const fullCards: OfferCard[] = sortedOffers.map((o) => ({ kind: 'full' as const, offer: o }));
    const pendingCards: OfferCard[] = offerStageApps
      .filter((a) => !manualByCompany.has(companyKey(a.company)))
      .map((app) => ({ kind: 'pending' as const, app }));
    return [...fullCards, ...pendingCards];
  }, [sortedOffers, offerStageApps]);

  const remove = (id: string) => {
    const next = offers.filter((o) => o.id !== id);
    setOffers(next);
    saveOffers(next);
    // Phase 14j: also wipe every per-offer localStorage entry so the offer
    // is GONE — not just visually hidden. Without this:
    //   - useSalaryBenchmark cached p25/p50/p75 lingers under
    //     `nice2job.benchmark.{id}` and gets attached to the next offer if
    //     `id` ever recycles (rare, but possible w/ manual id collisions)
    //   - useJobRunner state for negotiation / salary_benchmark stays in
    //     localStorage as orphaned `nice2job.job.*-{id}` keys
    // Fail-soft: ignore quota / parse errors — the visual remove is the
    // user's only success criterion.
    try {
      const linked = [
        `nice2job.benchmark.${id}`,
        `nice2job.job.salary-benchmark-${id}`,
        `nice2job.job.negotiate-${id}`,
      ];
      for (const k of linked) localStorage.removeItem(k);
    } catch { /* noop */ }
  };

  // Leader: highest total comp among ACTIVE offers only. A rejected offer
  // shouldn't be flagged "Top total" because the user has explicitly taken
  // it off the table.
  const leader = offers
    .filter((o) => o.status !== 'rejected')
    .reduce<Offer | null>((best, o) => (!best || o.total > best.total ? o : best), null);

  // Cross-offer comparison (6-dim grid + LLM advisor) moved to /offers/compare
  // in Phase 14h. Dashboard stays focused on per-offer browsing.

  const openAdd = (pref?: { company: string; role: string }) => {
    setEditing(null);                  // make sure we're not in edit mode
    setPrefill(pref || null);
    setOpen(true);
  };
  const openEdit = (existing: Offer) => {
    setEditing(existing);
    setPrefill(null);
    setOpen(true);
  };

  return (
    <div className="page-enter">
      <AddOfferDrawer
        open={open}
        prefill={prefill}
        editing={editing}
        onClose={() => { setOpen(false); setPrefill(null); setEditing(null); }}
        onSave={(o) => {
          // Preserve id when editing — saved offers are identified by id,
          // and our localStorage benchmarks are keyed off it. Replace in
          // place; otherwise append. Also seed status + createdAt on
          // brand-new offers so they sort correctly from the first save.
          const enriched: Offer = editing
            ? { ...o, status: editing.status ?? 'active', createdAt: editing.createdAt ?? Date.now() }
            : { ...o, status: 'active', createdAt: Date.now() };
          const next = editing
            ? offers.map((x) => (x.id === enriched.id ? enriched : x))
            : [...offers, enriched];
          setOffers(next);
          saveOffers(next);
          setOpen(false);
          setPrefill(null);
          setEditing(null);
        }}
      />
      <PageHeader
        eyebrow="Pipeline · offers"
        title={<>{cards.length ? `${cards.length} offer${cards.length === 1 ? '' : 's'}` : 'No offers yet'}. <em style={{ fontStyle: 'italic', color: 'var(--pri)' }}>One decision.</em></>}
        sub={
          <>
            Weighted across mission, growth, culture, comp, pace, remote.
            {' '}Every application you move to <strong>Offer</strong> stage on
            {' '}<a
              onClick={() => nav('/apps')}
              style={{ color: 'var(--pri)', cursor: 'pointer', textDecoration: 'underline' }}
            >Applications</a>
            {' '}lands here instantly — fill in the comp numbers to compare.
          </>
        }
        actions={
          <>
            {/* Sort selector. Persisted via localStorage. Always pushes
                rejected to the bottom regardless of mode. */}
            {offers.length >= 2 && (
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                title="Sort offers (rejected always go to the bottom)"
                style={{
                  padding: '6px 8px',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  fontSize: 12,
                  fontFamily: 'Inter, var(--cjk-sans), sans-serif',
                  background: 'var(--bg)',
                  color: 'var(--ink2)',
                }}
              >
                {(Object.keys(SORT_LABEL) as SortMode[]).map((k) => (
                  <option key={k} value={k}>Sort: {SORT_LABEL[k]}</option>
                ))}
              </select>
            )}
            {/* Compare lives on /offers/compare since Phase 14h. Disabled
                until you have ≥2 offers — comparison is meaningless with
                fewer. Per-card Negotiate stays inline on each card. */}
            <button
              className="btn"
              disabled={offers.length < 2}
              onClick={() => nav('/offers/compare')}
              title={offers.length < 2 ? 'Add at least 2 offers to compare' : 'Open the side-by-side comparison + AI advisor'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <GitCompare size={13} /> Compare &amp; analyze →
            </button>
            <button className="btn primary" onClick={() => openAdd()}>
              + Add offer
            </button>
          </>
        }
      />

      {cards.length === 0 ? (
        <div className="card empty">
          <span className="serif">No offers in the hopper.</span>
          Move a pipeline row to "Offer" stage or click + Add offer. Details never leave this browser.
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(cards.length, 3)}, 1fr)`, gap: 16, marginBottom: 24 }}>
            {cards.map((c) => {
              if (c.kind === 'full') {
                return (
                  <FullOfferCardView
                    key={c.offer.id}
                    offer={c.offer}
                    isLeader={c.offer.id === leader?.id}
                    onRemove={() => remove(c.offer.id)}
                    onEdit={() => openEdit(c.offer)}
                    onNegotiate={() => nav(`/negotiate/${c.offer.id}`)}
                    onToggleRejected={() => toggleRejected(c.offer.id)}
                  />
                );
              }
              // Pending card: Application with stage=Offer but no comp details yet.
              const a = c.app;
              return (
                <div
                  key={`pending-${a.id || a.num}`}
                  className="card"
                  style={{
                    padding: 22,
                    borderStyle: 'dashed',
                    borderColor: 'var(--line)',
                    position: 'relative',
                    background: 'rgba(0,0,0,0.01)',
                  }}
                >
                  <div className="chip" style={{ marginBottom: 14, fontSize: 10.5, background: 'var(--line2)', color: 'var(--ink2)' }}>
                    From pipeline
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <LogoSquare name={a.company} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{a.company}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{a.role}</div>
                    </div>
                  </div>
                  <div className="hair" style={{ margin: '14px 0' }} />
                  <div style={{ fontSize: 12.5, color: 'var(--ink2)', lineHeight: 1.6, marginBottom: 14 }}>
                    This offer is in your pipeline (from <code>applications.md</code>) but has no comp details yet. Fill in base / equity / bonus to compare it against your others.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn accent"
                      onClick={() => openAdd({ company: a.company, role: a.role })}
                    >
                      Fill in details →
                    </button>
                    <button
                      className="btn ghost"
                      onClick={() => nav(`/apps/${encodeURIComponent(a.id || String(a.num))}`)}
                      title="Open the application detail"
                    >
                      Open app
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cross-offer comparison & AI advisor moved to /offers/compare
              (Phase 14h). Footer prompt nudges users with ≥2 offers there. */}
          {offers.length >= 2 && (
            <div style={{ marginTop: 16, padding: '14px 18px', background: 'rgba(120, 90, 200, 0.04)', border: '1px solid rgba(120,90,200,0.25)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
              <GitCompare size={16} style={{ color: 'var(--pri)' }} />
              <div style={{ flex: 1, fontSize: 13, color: 'var(--ink2)' }}>
                You have {offers.length} offers. Run a side-by-side weighted comparison + market-gap analysis with the AI advisor.
              </div>
              <button
                className="btn accent"
                onClick={() => nav('/offers/compare')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                Compare &amp; analyze →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── FullOfferCardView ────────────────────────────────────────────────────
// Per-offer card. Promoted to its own component because it now uses the
// useSalaryBenchmark hook (one cache slot per offer). Negotiate is the
// primary action — surfaces inline on every offer, not just the leader.
function FullOfferCardView({
  offer, isLeader, onRemove, onEdit, onNegotiate, onToggleRejected,
}: {
  offer: Offer;
  isLeader: boolean;
  onRemove: () => void;
  onEdit: () => void;
  onNegotiate: () => void;
  onToggleRejected: () => void;
}) {
  const bench = useSalaryBenchmark(offer);
  const isRejected = offer.status === 'rejected';
  return (
    <div
      className="card"
      style={{
        padding: 22,
        // Rejected: muted border + greyed bg. Border drops to dashed so it
        // reads visually different from active cards even at a glance.
        borderColor: isRejected ? 'var(--line)' : isLeader ? 'var(--ink)' : 'var(--line)',
        borderWidth: isRejected ? 1 : isLeader ? 2 : 1,
        borderStyle: isRejected ? 'dashed' : 'solid',
        background: isRejected ? 'rgba(0,0,0,0.018)' : undefined,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // Greyscale + reduced opacity for entire rejected card. Transparent
        // border means hover lift won't fight the dashed style.
        opacity: isRejected ? 0.65 : 1,
      }}
    >
      {/* Status badge slot — rejected wins over Top total. */}
      {isRejected ? (
        <div className="chip warn" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Ban size={11} /> Rejected
        </div>
      ) : isLeader ? (
        <div className="chip primary" style={{ marginBottom: 14 }}>Top total</div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <LogoSquare name={offer.company} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 15,
              textDecoration: isRejected ? 'line-through' : undefined,
              color: isRejected ? 'var(--ink3)' : 'var(--ink)',
            }}
          >
            {offer.company}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink3)' }}>
            {offer.role}
            {offer.location && <span style={{ color: 'var(--ink3)' }}> · {offer.location}</span>}
          </div>
        </div>
        <button
          className="btn ghost"
          style={{ fontSize: 11, padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          onClick={onEdit}
          title="Edit comp details, scores, location — invalidates cached market benchmark if you change role/company/location"
        >
          <Pencil size={11} /> Edit
        </button>
        <button className="btn ghost" style={{ fontSize: 11 }} onClick={onRemove} title="Remove this offer">×</button>
      </div>
      <div className="hair" style={{ margin: '14px 0' }} />
      <div
        className="num"
        style={{
          fontSize: 40,
          lineHeight: 1,
          color: isRejected ? 'var(--ink3)' : 'var(--ink)',
          textDecoration: isRejected ? 'line-through' : undefined,
        }}
      >
        ${offer.total}k
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 4 }}>Total comp / year</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginTop: 16 }}>
        {(['base', 'equity', 'bonus'] as const).map((k) => (
          <div key={k}>
            <div style={{ fontSize: 11, color: 'var(--ink3)', textTransform: 'capitalize' }}>{k}</div>
            <div className="num" style={{ fontSize: 17 }}>${offer[k]}k</div>
          </div>
        ))}
      </div>

      {/* Market bar: prompts to estimate when empty, shows p25/p50/p75 +
          your offer marker after the LLM returns. Caches 7 days. Hidden on
          rejected cards — no point estimating market on a dead offer. */}
      {!isRejected && (
        <MarketBar
          totalK={offer.total}
          benchmark={bench.benchmark}
          isLoading={bench.isLoading}
          isStale={bench.isStale}
          error={bench.error}
          onFetch={bench.fetch}
          onRefresh={bench.benchmark ? bench.fetch : undefined}
        />
      )}

      {/* Action row: Negotiate (or hidden on rejected) + Reject/Reactivate. */}
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line2)', display: 'flex', gap: 8 }}>
        {!isRejected && (
          <button
            className="btn accent"
            onClick={onNegotiate}
            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            title="Build a counter-anchor email + verbal script tailored to this offer's market gap"
          >
            <MessageSquare size={13} /> Negotiate
          </button>
        )}
        <button
          className={isRejected ? 'btn accent' : 'btn ghost'}
          onClick={onToggleRejected}
          title={
            isRejected
              ? 'Bring this offer back to active — it will leave the bottom of the list and rejoin sorting'
              : 'Mark this offer as rejected — drops to bottom of list, stays here for the record'
          }
          style={{
            flex: isRejected ? 1 : undefined,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            color: isRejected ? undefined : 'var(--warn)',
            border: isRejected ? undefined : '1px solid rgba(180, 100, 40, 0.35)',
            padding: isRejected ? undefined : '6px 10px',
            fontSize: 12,
          }}
        >
          {isRejected ? <><Undo2 size={12} /> Reactivate</> : <><Ban size={12} /> Reject</>}
        </button>
      </div>
    </div>
  );
}

function AddOfferDrawer({
  open,
  prefill,
  editing,
  onClose,
  onSave,
}: {
  open: boolean;
  prefill: { company: string; role: string } | null;
  /** When set, drawer is in EDIT mode: prefill all fields from this offer
   *  and preserve the original `id` on save so localStorage keys (incl.
   *  the cached salary_benchmark) stay attached to the same record. */
  editing: Offer | null;
  onClose: () => void;
  onSave: (o: Offer) => void;
}) {
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [location, setLocation] = useState('');
  const [base, setBase] = useState('150');
  const [equity, setEquity] = useState('50');
  const [bonus, setBonus] = useState('15');
  const [scores, setScores] = useState<OfferScores>({ mission: 4, growth: 4, culture: 4, comp: 4, pace: 4, remote: 4 });

  // Three open modes: EDIT (full Offer) > PREFILL (pending pipeline only
  // gives company+role) > FRESH (defaults).
  useEffect(() => {
    if (!open) {
      // Closing — reset so next open doesn't inherit old form state
      setCompany('');
      setRole('');
      setLocation('');
      setBase('150');
      setEquity('50');
      setBonus('15');
      setScores({ mission: 4, growth: 4, culture: 4, comp: 4, pace: 4, remote: 4 });
      return;
    }
    if (editing) {
      setCompany(editing.company);
      setRole(editing.role);
      setLocation(editing.location || '');
      setBase(String(editing.base));
      setEquity(String(editing.equity));
      setBonus(String(editing.bonus));
      setScores(editing.scores);
    } else if (prefill) {
      setCompany(prefill.company);
      setRole(prefill.role);
    }
  }, [open, prefill, editing]);

  const total = (+base || 0) + (+equity || 0) + (+bonus || 0);
  const isEdit = !!editing;

  return (
    <Drawer open={open} onClose={onClose} width={480}>
      <div style={{ padding: '22px 24px', borderBottom: '1px solid var(--line)' }}>
        <div className="eyebrow" style={{ marginBottom: 4 }}>{isEdit ? 'Edit offer' : 'New offer'}</div>
        <div className="serif" style={{ fontSize: 26, letterSpacing: '-0.02em' }}>
          {isEdit ? `Update ${editing.company}'s offer.` : 'Another one in the hopper.'}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        <Field label="Company"><input value={company} onChange={(e) => setCompany(e.target.value)} style={inputStyle} /></Field>
        <Field label="Role"><input value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle} /></Field>
        <Field label="Location (optional but improves market estimate)">
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. London, San Francisco, Remote (US)"
            style={inputStyle}
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label="Base ($k)"><input type="number" value={base} onChange={(e) => setBase(e.target.value)} style={inputStyle} /></Field>
          <Field label="Equity ($k)"><input type="number" value={equity} onChange={(e) => setEquity(e.target.value)} style={inputStyle} /></Field>
          <Field label="Bonus ($k)"><input type="number" value={bonus} onChange={(e) => setBonus(e.target.value)} style={inputStyle} /></Field>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink3)', margin: '-4px 0 14px' }}>Total: ${total}k/yr</div>
        <div className="tab" style={{ marginBottom: 8 }}>Score each dimension (0–5)</div>
        {DIMS.map((k) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 80, fontSize: 12, textTransform: 'capitalize' }}>{k}</div>
            <input
              type="range" min={0} max={5} step={0.1}
              value={scores[k]}
              onChange={(e) => setScores({ ...scores, [k]: +e.target.value })}
              style={{ flex: 1, accentColor: 'var(--pri)' }}
            />
            <div className="num" style={{ width: 32, fontSize: 15 }}>{scores[k].toFixed(1)}</div>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid var(--line)', padding: '16px 24px', display: 'flex', gap: 10 }}>
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <div style={{ flex: 1 }} />
        <button
          className="btn accent lg"
          disabled={!company || !role}
          onClick={() =>
            onSave({
              // Preserve id when editing — keeps the same localStorage cache
              // keys (offer entry, salary_benchmark) attached to this record.
              id: editing?.id ?? `offer-${Date.now()}`,
              company, role,
              location: location.trim() || undefined,
              base: +base, equity: +equity, bonus: +bonus, total,
              scores,
            })
          }
        >
          {isEdit ? 'Save changes' : 'Save offer'}
        </button>
      </div>
    </Drawer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="tab" style={{ marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6,
  fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)',
};
